<template>
  <div class="conflicts-page">
    <div class="page-wrapper">
      <header class="page-header">
        <div class="header-text">
          <h1><i class="mdi mdi-shield-alert"></i> Biometrics Conflict Log</h1>
          <p>Scans that were rejected or could not be matched to an EdClock record.</p>
        </div>
        <div class="header-actions">
          <router-link to="/conflicts/scans" class="btn ghost">
            <i class="mdi mdi-face-recognition"></i> Successful scans
          </router-link>
          <router-link to="/conflicts/students" class="btn ghost">
            <i class="mdi mdi-account-group"></i> By student
          </router-link>
          <button class="btn ghost" :disabled="loading" @click="refresh">
            <i class="mdi" :class="loading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></i>
            Refresh
          </button>
          <button class="btn primary" :disabled="scanning" @click="runScan">
            <i class="mdi" :class="scanning ? 'mdi-loading mdi-spin' : 'mdi-database-search'"></i>
            {{ scanning ? 'Scanning…' : 'Run integrity scan' }}
          </button>
        </div>
      </header>

      <div v-if="toast" class="toast" :class="toast.kind">
        <i class="mdi" :class="toast.kind === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>
        <span>{{ toast.text }}</span>
        <button class="toast-close" @click="toast = null"><i class="mdi mdi-close"></i></button>
      </div>

      <section class="stat-grid">
        <div class="stat-card critical">
          <span class="stat-label">Open · critical</span>
          <span class="stat-value">{{ stats.openBySeverity.critical || 0 }}</span>
        </div>
        <div class="stat-card warning">
          <span class="stat-label">Open · warning</span>
          <span class="stat-value">{{ stats.openBySeverity.warning || 0 }}</span>
        </div>
        <div class="stat-card resolved">
          <span class="stat-label">Resolved</span>
          <span class="stat-value">{{ stats.byStatus.resolved || 0 }}</span>
        </div>
        <div class="stat-card total">
          <span class="stat-label">Total logged</span>
          <span class="stat-value">{{ stats.total || 0 }}</span>
        </div>
      </section>

      <section class="filters">
        <div class="status-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            class="tab"
            :class="{ active: filters.status === tab.value }"
            @click="setFilter('status', tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="filter-controls">
          <select v-model="filters.type" @change="reload">
            <option value="all">All types</option>
            <option v-for="type in conflictTypes" :key="type" :value="type">
              {{ typeLabel(type) }}
            </option>
          </select>

          <div class="search-box">
            <i class="mdi mdi-magnify"></i>
            <input
              v-model="filters.q"
              type="text"
              placeholder="Search name, id or message…"
              @input="debouncedReload"
            />
          </div>
        </div>
      </section>

      <section class="table-card">
        <div v-if="loading" class="empty-state">
          <i class="mdi mdi-loading mdi-spin"></i>
          <p>Loading conflicts…</p>
        </div>

        <div v-else-if="conflicts.length === 0" class="empty-state">
          <i class="mdi mdi-check-circle-outline"></i>
          <p>No conflicts match these filters.</p>
        </div>

        <table v-else class="conflict-table">
          <thead>
            <tr>
              <th class="col-sev"></th>
              <th>Type</th>
              <th>Student</th>
              <th>Detail</th>
              <th>When</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="conflict in conflicts"
              :key="conflict._id"
              :class="{ selected: selected && selected._id === conflict._id }"
              @click="openDetail(conflict)"
            >
              <td class="col-sev">
                <span class="sev-dot" :class="conflict.severity"></span>
              </td>
              <td>
                <span class="type-badge" :class="conflict.type.toLowerCase()">
                  {{ typeLabel(conflict.type) }}
                </span>
              </td>
              <td class="col-student">
                <div class="student-name">{{ conflict.studentName || '—' }}</div>
                <div class="student-id">{{ conflict.biometricsStudentId || '' }}</div>
              </td>
              <td class="col-message">{{ conflict.message }}</td>
              <td class="col-when">{{ formatTime(conflict.occurredAt) }}</td>
              <td>
                <span class="status-badge" :class="conflict.status">{{ conflict.status }}</span>
              </td>
              <td class="col-chevron"><i class="mdi mdi-chevron-right"></i></td>
            </tr>
          </tbody>
        </table>

        <div v-if="pagination.pages > 1" class="pagination">
          <button class="btn ghost" :disabled="pagination.page <= 1" @click="goToPage(pagination.page - 1)">
            <i class="mdi mdi-chevron-left"></i> Previous
          </button>
          <span>Page {{ pagination.page }} of {{ pagination.pages }} · {{ pagination.total }} entries</span>
          <button
            class="btn ghost"
            :disabled="pagination.page >= pagination.pages"
            @click="goToPage(pagination.page + 1)"
          >
            Next <i class="mdi mdi-chevron-right"></i>
          </button>
        </div>
      </section>
    </div>

    <!-- Detail drawer -->
    <div v-if="selected" class="drawer-backdrop" @click.self="closeDetail">
      <aside class="drawer">
        <header class="drawer-header">
          <div>
            <span class="type-badge" :class="selected.type.toLowerCase()">
              {{ typeLabel(selected.type) }}
            </span>
            <h2>{{ selected.studentName || 'Unidentified scan' }}</h2>
            <p class="drawer-time">{{ formatTime(selected.occurredAt) }}</p>
          </div>
          <button class="icon-btn" @click="closeDetail"><i class="mdi mdi-close"></i></button>
        </header>

        <div class="drawer-body">
          <p class="drawer-message">{{ selected.message }}</p>

          <dl class="detail-grid">
            <div>
              <dt>Biometrics ID</dt>
              <dd>{{ selected.biometricsStudentId || '—' }}</dd>
            </div>
            <div>
              <dt>EdClock ID</dt>
              <dd :class="{ missing: edClockIdIsProblem }">{{ edClockIdDisplay }}</dd>
            </div>
            <div>
              <dt>Mode</dt>
              <dd>{{ selected.mode || '—' }}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{{ selected.source || '—' }}</dd>
            </div>
            <div v-if="selected.threshold">
              <dt>Threshold</dt>
              <dd>{{ selected.threshold }}%</dd>
            </div>
            <div v-if="selected.gap != null">
              <dt>Top-two gap</dt>
              <dd>{{ selected.gap.toFixed(2) }}%</dd>
            </div>
          </dl>

          <div v-if="selected.candidates && selected.candidates.length" class="candidates">
            <h3>Match candidates</h3>
            <p v-if="canSelectCandidate" class="hint select-hint">
              Pick the student this scan actually was. The others are recorded as rejected.
            </p>

            <component
              :is="canSelectCandidate ? 'label' : 'div'"
              v-for="candidate in selected.candidates"
              :key="candidate.studentId"
              class="candidate"
              :class="{
                selectable: canSelectCandidate,
                chosen: chosenCandidate === candidate.studentId
              }"
            >
              <input
                v-if="canSelectCandidate"
                v-model="chosenCandidate"
                class="candidate-radio"
                type="radio"
                :value="candidate.studentId"
              />
              <div class="candidate-body">
                <div class="candidate-head">
                  <span class="candidate-name">{{ candidate.fullName || candidate.studentId }}</span>
                  <span class="candidate-score">{{ candidate.similarity.toFixed(2) }}%</span>
                </div>
                <div class="score-bar">
                  <div
                    class="score-fill"
                    :class="{ over: candidate.similarity >= (selected.threshold || 99) }"
                    :style="{ width: scoreWidth(candidate.similarity) }"
                  ></div>
                </div>
                <div class="candidate-meta">
                  EdClock {{ candidate.edClockStudentId || '—' }} · biometrics {{ candidate.studentId }}
                </div>
                <div v-if="canSelectCandidate && !candidate.edClockStudentId" class="candidate-warn">
                  <i class="mdi mdi-alert"></i> Not linked to EdClock — clocking will fail for them.
                </div>
              </div>
            </component>
          </div>

          <div v-if="selected.status !== 'open'" class="resolution-summary">
            <h3>Resolution</h3>
            <p>
              <strong>{{ actionLabel(selected.resolution && selected.resolution.action) }}</strong>
              by {{ (selected.resolution && selected.resolution.resolvedBy) || 'admin' }}
              on {{ formatTime(selected.resolution && selected.resolution.resolvedAt) }}
            </p>
            <p v-if="selected.resolution && selected.resolution.selectedStudentName" class="picked">
              Confirmed as <strong>{{ selected.resolution.selectedStudentName }}</strong>
              (EdClock {{ selected.resolution.selectedEdClockStudentId || '—' }})<span
                v-if="selected.resolution.rejectedStudentIds && selected.resolution.rejectedStudentIds.length"
              >, rejecting {{ selected.resolution.rejectedStudentIds.length }} other candidate(s)</span>.
            </p>
            <p v-if="selected.resolution && selected.resolution.note" class="note">
              “{{ selected.resolution.note }}”
            </p>
            <button class="btn ghost" :disabled="resolving" @click="reopen">
              <i class="mdi mdi-restore"></i> Reopen
            </button>
          </div>

          <div v-else class="resolve-panel">
            <h3>Resolve</h3>

            <div v-if="canLink" class="link-block">
              <label>Link to the correct EdClock student</label>
              <div class="search-box">
                <i class="mdi mdi-magnify"></i>
                <input
                  v-model="edclockQuery"
                  type="text"
                  placeholder="Search EdClock by name…"
                  @input="debouncedEdClockSearch"
                />
                <i v-if="edclockSearching" class="mdi mdi-loading mdi-spin"></i>
              </div>

              <ul v-if="edclockResults.length" class="edclock-results">
                <li v-for="student in edclockResults" :key="student.id">
                  <div>
                    <span class="result-name">{{ student.name }}</span>
                    <span class="result-id">ID {{ student.id }}</span>
                  </div>
                  <button class="btn small primary" :disabled="resolving" @click="link(student.id)">
                    Link
                  </button>
                </li>
              </ul>
              <p v-else-if="edclockQuery.length >= 2 && !edclockSearching" class="hint">
                No EdClock students found.
              </p>
            </div>

            <label class="note-label">Note (optional)</label>
            <textarea v-model="note" rows="2" placeholder="Why was this resolved this way?"></textarea>

            <div class="resolve-actions">
              <button
                v-if="canSelectCandidate"
                class="btn primary"
                :disabled="resolving || !chosenCandidate"
                @click="confirmCandidate"
              >
                <i class="mdi mdi-account-check"></i>
                {{ chosenCandidate ? 'Confirm selected student' : 'Select a candidate above' }}
              </button>
              <button class="btn danger" :disabled="resolving" @click="resolve('marked_false_match')">
                <i class="mdi mdi-account-alert"></i> False match
              </button>
              <button
                v-if="!canSelectCandidate"
                class="btn success"
                :disabled="resolving"
                @click="resolve('confirmed_identity')"
              >
                <i class="mdi mdi-account-check"></i> Identity confirmed
              </button>
              <button class="btn ghost" :disabled="resolving" @click="resolve('ignored')">
                <i class="mdi mdi-eye-off"></i> Ignore
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const TYPE_LABELS = {
  AMBIGUOUS_FACE_MATCH: 'Ambiguous match',
  LOW_CONFIDENCE_MATCH: 'Low confidence',
  UNLINKED_EDCLOCK_ID: 'Unlinked EdClock ID',
  ORPHANED_FACE_INDEX: 'Orphaned face index',
  DUPLICATE_EDCLOCK_LINK: 'Duplicate EdClock link',
};

const ACTION_LABELS = {
  link_edclock_id: 'Linked EdClock ID',
  select_candidate: 'Confirmed correct student',
  marked_false_match: 'Marked as false match',
  confirmed_identity: 'Identity confirmed',
  ignored: 'Ignored',
};

export default {
  name: 'ConflictLogsView',
  data() {
    return {
      conflicts: [],
      stats: { total: 0, byStatus: {}, openByType: {}, openBySeverity: {} },
      pagination: { page: 1, pages: 1, total: 0, limit: 25 },
      filters: { status: 'open', type: 'all', severity: 'all', q: '' },
      statusTabs: [
        { value: 'open', label: 'Open' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'ignored', label: 'Ignored' },
        { value: 'all', label: 'All' },
      ],
      conflictTypes: Object.keys(TYPE_LABELS),
      loading: false,
      scanning: false,
      resolving: false,
      selected: null,
      chosenCandidate: null,
      note: '',
      edclockQuery: '',
      edclockResults: [],
      edclockSearching: false,
      toast: null,
      reloadTimer: null,
      searchTimer: null,
    };
  },
  computed: {
    canLink() {
      return this.selected && ['UNLINKED_EDCLOCK_ID', 'DUPLICATE_EDCLOCK_LINK'].includes(this.selected.type);
    },
    // A missing id is only a fault for the types that are *about* a broken
    // link. A rejected scan has no id because it resolved to nobody, which is
    // the intended outcome rather than something to repair.
    edClockIdIsProblem() {
      return Boolean(this.selected) && !this.selected.edClockStudentId && this.canLink;
    },
    edClockIdDisplay() {
      if (!this.selected) return '—';
      if (this.selected.edClockStudentId) return this.selected.edClockStudentId;
      return this.canLink ? 'not linked' : 'n/a — no single match';
    },
    // A rejected scan is the one case where the operator, not the system,
    // decides who it was.
    canSelectCandidate() {
      return Boolean(
        this.selected &&
          this.selected.status === 'open' &&
          this.selected.candidates &&
          this.selected.candidates.length > 0 &&
          ['AMBIGUOUS_FACE_MATCH', 'LOW_CONFIDENCE_MATCH'].includes(this.selected.type)
      );
    },
  },
  created() {
    this.refresh();
  },
  beforeUnmount() {
    clearTimeout(this.reloadTimer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    apiUrl(path) {
      const base = String(process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      return `${base}${path}`;
    },
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || action || '—';
    },
    formatTime(value) {
      return value ? moment(value).format('MMM D, YYYY · h:mm A') : '—';
    },
    scoreWidth(similarity) {
      // Everything lands between 80 and 100, so rescale for a readable bar.
      return `${Math.max(0, Math.min(100, (similarity - 80) * 5))}%`;
    },
    showToast(text, kind = 'success') {
      this.toast = { text, kind };
      setTimeout(() => {
        this.toast = null;
      }, 5000);
    },

    setFilter(key, value) {
      this.filters[key] = value;
      this.reload();
    },
    reload() {
      this.pagination.page = 1;
      this.refresh();
    },
    debouncedReload() {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(this.reload, 350);
    },
    goToPage(page) {
      this.pagination.page = page;
      this.refresh();
    },

    async refresh() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          status: this.filters.status,
          type: this.filters.type,
          severity: this.filters.severity,
          page: String(this.pagination.page),
          limit: String(this.pagination.limit),
        });
        if (this.filters.q) params.set('q', this.filters.q);

        const [listResponse, statsResponse] = await Promise.all([
          fetch(this.apiUrl(`/api/conflicts?${params.toString()}`)),
          fetch(this.apiUrl('/api/conflicts/stats')),
        ]);

        if (!listResponse.ok) throw new Error('Failed to load conflicts');

        const list = await listResponse.json();
        this.conflicts = list.items;
        this.pagination = { ...this.pagination, ...list.pagination };

        if (statsResponse.ok) {
          const stats = await statsResponse.json();
          this.stats = {
            total: stats.total,
            byStatus: stats.byStatus || {},
            openByType: stats.openByType || {},
            openBySeverity: stats.openBySeverity || {},
          };
        }
      } catch (error) {
        this.showToast(error.message || 'Failed to load conflicts', 'error');
      } finally {
        this.loading = false;
      }
    },

    async runScan() {
      this.scanning = true;
      try {
        const response = await fetch(this.apiUrl('/api/conflicts/scan'), { method: 'POST' });
        if (!response.ok) throw new Error('Integrity scan failed');

        const result = await response.json();
        this.showToast(
          `Scanned ${result.scanned} students · ${result.created} new conflict(s), ` +
            `${result.alreadyOpen} already open.`
        );
        await this.refresh();
      } catch (error) {
        this.showToast(error.message || 'Integrity scan failed', 'error');
      } finally {
        this.scanning = false;
      }
    },

    openDetail(conflict) {
      this.selected = conflict;
      this.note = '';
      this.chosenCandidate = null;
      this.edclockResults = [];
      this.edclockQuery = conflict.studentName || '';
      if (this.canLink && this.edclockQuery.length >= 2) {
        this.searchEdClock();
      }
    },
    closeDetail() {
      this.selected = null;
    },

    debouncedEdClockSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.searchEdClock, 350);
    },
    async searchEdClock() {
      const q = this.edclockQuery.trim();
      if (q.length < 2) {
        this.edclockResults = [];
        return;
      }

      this.edclockSearching = true;
      try {
        const response = await fetch(
          this.apiUrl(`/api/conflicts/edclock-search?q=${encodeURIComponent(q)}`)
        );
        if (!response.ok) throw new Error('EdClock search failed');
        const data = await response.json();
        this.edclockResults = data.students || [];
      } catch (error) {
        this.edclockResults = [];
        this.showToast(error.message || 'EdClock search failed', 'error');
      } finally {
        this.edclockSearching = false;
      }
    },

    link(edClockStudentId) {
      return this.resolve('link_edclock_id', { edClockStudentId });
    },

    confirmCandidate() {
      if (!this.chosenCandidate) return;
      return this.resolve('select_candidate', { candidateStudentId: this.chosenCandidate });
    },

    async resolve(action, extra = {}) {
      if (!this.selected) return;

      this.resolving = true;
      try {
        const response = await fetch(this.apiUrl(`/api/conflicts/${this.selected._id}/resolve`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, note: this.note, ...extra }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to resolve');

        const resolved = data.conflict || {};
        this.showToast(
          action === 'link_edclock_id'
            ? `Linked to EdClock ID ${extra.edClockStudentId}. Clock-out will work now.`
            : action === 'select_candidate'
              ? `Recorded as ${resolved.studentName || 'the selected student'}.`
              : `Marked as "${this.actionLabel(action)}".`
        );
        this.selected = null;
        await this.refresh();
      } catch (error) {
        this.showToast(error.message || 'Failed to resolve', 'error');
      } finally {
        this.resolving = false;
      }
    },

    async reopen() {
      if (!this.selected) return;

      this.resolving = true;
      try {
        const response = await fetch(this.apiUrl(`/api/conflicts/${this.selected._id}/reopen`), {
          method: 'POST',
        });
        if (!response.ok) throw new Error('Failed to reopen');

        this.showToast('Conflict reopened.');
        this.selected = null;
        await this.refresh();
      } catch (error) {
        this.showToast(error.message || 'Failed to reopen', 'error');
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.conflicts-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  padding: 2rem 1rem 4rem;
}

.page-wrapper {
  max-width: 1240px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.header-text h1 {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.35rem;
}

.header-text h1 i {
  color: #6366f1;
}

.header-text p {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  gap: 0.6rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.1rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.primary {
  background: #6366f1;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn.ghost {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.btn.ghost:hover:not(:disabled) {
  background: #f1f5f9;
}

.btn.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.btn.danger:hover:not(:disabled) {
  background: #fecaca;
}

.btn.success {
  background: #dcfce7;
  color: #15803d;
}

.btn.success:hover:not(:disabled) {
  background: #bbf7d0;
}

.btn.small {
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
}

.icon-btn {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
}

.icon-btn:hover {
  color: #475569;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.toast.success {
  background: #dcfce7;
  color: #166534;
}

.toast.error {
  background: #fee2e2;
  color: #991b1b;
}

.toast-close {
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
  border-left: 4px solid #cbd5e1;
}

.stat-card.critical {
  border-left-color: #ef4444;
}

.stat-card.warning {
  border-left-color: #f59e0b;
}

.stat-card.resolved {
  border-left-color: #22c55e;
}

.stat-card.total {
  border-left-color: #6366f1;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.status-tabs {
  display: flex;
  background: #fff;
  border-radius: 10px;
  padding: 0.25rem;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.06);
}

.tab {
  border: none;
  background: transparent;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab.active {
  background: #6366f1;
  color: #fff;
}

.filter-controls {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

select,
.search-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  font-size: 0.875rem;
  color: #334155;
}

select {
  cursor: pointer;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 260px;
}

.search-box i {
  color: #94a3b8;
}

.search-box input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.875rem;
  color: #334155;
  background: transparent;
}

.table-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
}

.conflict-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.conflict-table th {
  text-align: left;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.conflict-table td {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.conflict-table tbody tr {
  cursor: pointer;
  transition: background 0.15s ease;
}

.conflict-table tbody tr:hover,
.conflict-table tbody tr.selected {
  background: #f8fafc;
}

.col-sev {
  width: 28px;
  padding-right: 0 !important;
}

.sev-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #cbd5e1;
}

.sev-dot.critical {
  background: #ef4444;
}

.sev-dot.warning {
  background: #f59e0b;
}

.sev-dot.info {
  background: #3b82f6;
}

.type-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  background: #e2e8f0;
  color: #475569;
}

.type-badge.ambiguous_face_match {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.low_confidence_match {
  background: #fee2e2;
  color: #991b1b;
}

.type-badge.unlinked_edclock_id {
  background: #e0e7ff;
  color: #3730a3;
}

.type-badge.orphaned_face_index {
  background: #fce7f3;
  color: #9d174d;
}

.type-badge.duplicate_edclock_link {
  background: #ccfbf1;
  color: #115e59;
}

.col-student {
  min-width: 150px;
}

.student-name {
  font-weight: 600;
  color: #1e293b;
}

.student-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  color: #94a3b8;
}

.col-message {
  color: #475569;
  max-width: 420px;
  line-height: 1.45;
}

.col-when {
  color: #64748b;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-badge.open {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.resolved {
  background: #dcfce7;
  color: #166534;
}

.status-badge.ignored {
  background: #e2e8f0;
  color: #475569;
}

.col-chevron {
  color: #cbd5e1;
  width: 24px;
}

.empty-state {
  padding: 4rem 1rem;
  text-align: center;
  color: #94a3b8;
}

.empty-state i {
  font-size: 2.5rem;
  opacity: 0.6;
}

.empty-state p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
  font-weight: 500;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  font-size: 0.85rem;
  color: #64748b;
}

/* Drawer */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
  z-index: 9999;
}

.drawer {
  width: min(520px, 100%);
  background: #fff;
  height: 100%;
  overflow-y: auto;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.15);
  animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideInRight {
  from {
    transform: translateX(30px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}

.drawer-header h2 {
  font-size: 1.3rem;
  color: #1e293b;
  margin: 0.6rem 0 0.25rem;
}

.drawer-time {
  margin: 0;
  font-size: 0.82rem;
  color: #94a3b8;
}

.drawer-body {
  padding: 1.5rem;
}

.drawer-message {
  background: #f8fafc;
  border-left: 3px solid #6366f1;
  padding: 0.85rem 1rem;
  border-radius: 0 10px 10px 0;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0 0 1.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 0 0 1.75rem;
}

.detail-grid dt {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 0.2rem;
}

.detail-grid dd {
  margin: 0;
  font-size: 0.88rem;
  color: #1e293b;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
}

.detail-grid dd.missing {
  color: #dc2626;
  font-weight: 600;
}

.candidates h3,
.resolve-panel h3,
.resolution-summary h3 {
  font-size: 0.95rem;
  color: #1e293b;
  margin: 0 0 0.85rem;
}

.candidate {
  display: block;
  margin-bottom: 1rem;
}

.select-hint {
  margin: -0.4rem 0 0.85rem;
}

.candidate.selectable {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.candidate.selectable:hover {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.candidate.selectable.chosen {
  border-color: #6366f1;
  background: #eef2ff;
}

.candidate-radio {
  margin-top: 0.25rem;
  accent-color: #6366f1;
  flex-shrink: 0;
}

.candidate-body {
  flex: 1;
  min-width: 0;
}

.candidate-warn {
  margin-top: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b45309;
}

.picked {
  font-size: 0.88rem;
  color: #475569;
}

.candidate-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.3rem;
}

.candidate-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.88rem;
}

.candidate-score {
  font-weight: 700;
  color: #6366f1;
  font-size: 0.88rem;
}

.score-bar {
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  background: #f59e0b;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.score-fill.over {
  background: #22c55e;
}

.candidate-meta {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.resolve-panel,
.resolution-summary {
  margin-top: 1.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
}

.resolution-summary p {
  font-size: 0.88rem;
  color: #475569;
  margin: 0 0 0.5rem;
}

.resolution-summary .note {
  font-style: italic;
  color: #64748b;
}

.link-block {
  margin-bottom: 1.25rem;
}

.link-block label,
.note-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.4rem;
}

.edclock-results {
  list-style: none;
  padding: 0;
  margin: 0.6rem 0 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.edclock-results li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid #f1f5f9;
}

.edclock-results li:last-child {
  border-bottom: none;
}

.result-name {
  display: block;
  font-size: 0.86rem;
  font-weight: 600;
  color: #1e293b;
}

.result-id {
  font-size: 0.72rem;
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.hint {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0.5rem 0 0;
}

textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.86rem;
  font-family: inherit;
  color: #334155;
  resize: vertical;
  outline: none;
}

textarea:focus {
  border-color: #a5b4fc;
}

.resolve-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .conflict-table .col-message,
  .conflict-table th:nth-child(4) {
    display: none;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
